import type { AccountCoverage, CoverageStatus } from './types'

const reconcileStatusText = (
  counts: Record<CoverageStatus, number>,
): string => {
  if (counts.needs_review > 0) return `Needs review (${counts.needs_review})`
  if (counts.unverifiable > 0) return `Unverifiable (${counts.unverifiable})`
  if (counts.reconciled === 0 && counts.not_checked > 0) return 'Not checked'
  return 'Reconciled'
}

// Reconciliation is premium, so an account without it gets the coverage columns only. Nothing here
// may hint at a verdict, and its verified-gap count is always 0 because proving a gap needs balances.
export function exportCoverageCSV(
  accounts: AccountCoverage[],
  slug?: string,
  { includeReconciliation = true }: { includeReconciliation?: boolean } = {},
): void {
  const headers = [
    'Account Number',
    'Role',
    'Account Type',
    'Date Range',
    'Coverage Status',
    ...(includeReconciliation ? ['Reconcile Status'] : []),
    'Missing Months',
    ...(includeReconciliation ? ['Verified Gap Months'] : []),
  ]

  const rows = accounts.map((account) => [
    account.id,
    account.parentId ? `Member of ${account.parentId}` : 'Primary',
    account.type ?? '',
    account.rangeText,
    account.missingText === 'Complete' ? 'Complete' : 'Missing',
    ...(includeReconciliation
      ? [
          account.parentId
            ? `Reconciled under ${account.parentId}`
            : reconcileStatusText(account.statusCounts),
        ]
      : []),
    account.missingText === 'Complete' ? '' : account.missingText,
    // Months no statement spans but the surrounding balances chain: proven quiet, not missing.
    ...(includeReconciliation
      ? [String(account.statusCounts.verified_gap)]
      : []),
  ])

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute(
    'download',
    `${slug ? `${slug}-` : ''}statement-coverage.csv`,
  )
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
